import React from 'react';
import styles from './StructuredModuleRenderer.module.scss';
import type { WebappStructuredContentModule } from '@eventbrite/event-listing-service-client';
import classNames from 'classnames';
import { LazyImage } from './components/LazyImage';
import { LazyVideo } from './components/LazyVideo';
import { qualifyUserGeneratedLinks } from '@/utils/qualifyUserGeneratedLinks';

type ModuleType = WebappStructuredContentModule['type'];

export const MODULE_TYPES: Record<Uppercase<ModuleType>, ModuleType> = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
} as const;

export const TEXT_ALIGNMENTS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

type TextContentProps = {
  text: string;
  alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS];
};

type ImageContentProps = {
  url: string;
  alt?: string;
  caption?: string;
  // Exposed by event-listing but not yet in the published client type.
  originalUrl?: string;
  width?: number;
  height?: number;
};

const IMAGE_SIZES = '(max-width: 940px) 100vw, 940px';

type VideoContentProps = {
  url: string;
  altText?: string;
};

const getTextAlignment = (alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS]) => {
  switch (alignment) {
    case TEXT_ALIGNMENTS.LEFT:
      return styles.textLeft;
    case TEXT_ALIGNMENTS.CENTER:
      return styles.textCenter;
    case TEXT_ALIGNMENTS.RIGHT:
      return styles.textRight;
  }
};

const TextContent = ({ text, alignment }: TextContentProps) => (
  <div className={getTextAlignment(alignment)} dangerouslySetInnerHTML={{ __html: qualifyUserGeneratedLinks(text) }} />
);

const VideoContent = ({ url, altText }: VideoContentProps) => (
  <div className={styles.video}>
    <LazyVideo src={url} altText={altText} />
  </div>
);

const ImageContent = ({ url, alt, caption, originalUrl, width = 0, height = 0 }: ImageContentProps) => (
  <div className={styles.imageContent}>
    {originalUrl && width > 0 && height > 0 ? (
      <LazyImage
        src={originalUrl}
        alt={alt || ''}
        width={width}
        height={height}
        sizes={IMAGE_SIZES}
        style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
      />
    ) : (
      // Legacy responses only carry a pre-sized URL (capped at 1200x740):
      // render it at natural size to avoid blurry upscaling.
      <LazyImage src={url} alt={alt || ''} preserveIntrinsicSize style={{ maxWidth: '100%', width: 'fit-content' }} />
    )}
    {caption && <span className={styles.caption}>{caption}</span>}
  </div>
);

export type StructuredModuleRendererProps = {
  module: WebappStructuredContentModule;
};

export const StructuredModuleRenderer = ({ module }: StructuredModuleRendererProps) => {
  const className = classNames(styles.structuredContent, styles[module.type]);

  switch (module.type) {
    case MODULE_TYPES.TEXT:
      const textContent = module as TextContentProps;
      return (
        <div className={className} data-testid="text-content">
          <TextContent text={textContent.text} alignment={textContent.alignment} />
        </div>
      );

    case MODULE_TYPES.IMAGE:
      const imageContent = module as ImageContentProps;
      if (!imageContent.url) return <></>;
      return (
        <div className={className} data-testid="image-content">
          <ImageContent
            url={imageContent.url}
            alt={imageContent.alt}
            caption={imageContent.caption}
            originalUrl={imageContent.originalUrl}
            width={imageContent.width}
            height={imageContent.height}
          />
        </div>
      );
    case MODULE_TYPES.VIDEO:
      const videoContent = module as VideoContentProps;
      return (
        <div className={className} data-testid="video-content">
          <VideoContent url={videoContent.url} altText={videoContent.altText} />
        </div>
      );
    default:
      return <></>;
  }
};
